import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Router } from '@angular/router';
import { environment } from '@lib/environments/environment';
import { Licence } from '@lib/shared/models/licence.model';
import { User } from '@lib/shared/models/user.model';
import { ToastService } from '@lib/shared/services/toast.service';
import { Timestamp } from 'firebase/firestore';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ExpiredLicenceGuard {
  constructor(
    private router: Router,
    private afAuth: AngularFireAuth,
    private afs: AngularFirestore,
    private toastService: ToastService,
  ) {}

  async canActivate(): Promise<boolean> {
    try {
      const auth = await this.afAuth.currentUser;
      if (!auth) {
        console.warn('[ExpiredLicenceGuard] No authenticated user; redirecting to login');
        this.router.navigate(['/login']);
        return false;
      }

      console.info('[ExpiredLicenceGuard] Checking subscription for user', auth.uid);
      const userDoc = this.afs.doc<User>(`users/${auth.uid}`);
      const user = await firstValueFrom(userDoc.valueChanges());

      if (!user) {
        console.warn('[ExpiredLicenceGuard] Missing user profile; redirecting to login', auth.uid);
        this.router.navigate(['/login']);
        return false;
      }

      const licencePath = typeof user?.licence === 'string' ? user.licence : user?.licence?.path;

      if (!licencePath) {
        console.warn('[ExpiredLicenceGuard] Missing subscription reference on profile', auth.uid);
        this.router.navigate(['/no-licence']);
        return false;
      }

      console.info('[ExpiredLicenceGuard] Loading subscription', licencePath);
      const licence = await firstValueFrom(this.afs.doc<Licence>(licencePath).valueChanges());
      const notExpiredName = environment.appType === 'pe' ? 'notExpired' : 'outdoorProNotExpired';

      if (!licence) {
        console.warn('[ExpiredLicenceGuard] Subscription not found', licencePath);
        this.router.navigate(['/no-licence']);
        return false;
      }

      const newUserDoc = {
        notExpired: !!(this.dateIsBefore(licence?.expiresAt) && this.dateIsAfter(licence?.validFrom)),
        outdoorProNotExpired: !!(
          this.dateIsBefore(licence?.outdoorProExpiresAt) && this.dateIsAfter(licence?.outdoorProValidFrom)
        ),
      };

      console.info('[ExpiredLicenceGuard] Subscription status evaluated', {
        trial: Boolean(licence?.trial),
        notExpired: newUserDoc.notExpired,
        outdoorProNotExpired: newUserDoc.outdoorProNotExpired,
      });

      await userDoc.update(newUserDoc);

      if (!newUserDoc[notExpiredName]) {
        console.warn('[ExpiredLicenceGuard] Subscription expired for app access', {
          app: environment.appType,
          licencePath,
        });
        this.router.navigate(['/licence-expired']);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[ExpiredLicenceGuard] Failed while evaluating subscription', error);
      this.toastService.showDissmissableErrorToast(
        'We could not validate your subscription right now. Please try again or contact support.',
      );
      this.router.navigate(['/login']);
      return false;
    }
  }

  private dateIsBefore(date: Timestamp | Date): boolean {
    const normalizedDate = this.normalizeDate(date);
    return Boolean(normalizedDate && moment().isSameOrBefore(normalizedDate));
  }

  private dateIsAfter(date: Timestamp | Date): boolean {
    const normalizedDate = this.normalizeDate(date);
    return Boolean(normalizedDate && moment().isSameOrAfter(normalizedDate));
  }

  private normalizeDate(date: Timestamp | Date): Date | null {
    if (!date) {
      return null;
    }

    if (date instanceof Date) {
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof (date as Timestamp).toDate === 'function') {
      const convertedDate = (date as Timestamp).toDate();
      return Number.isNaN(convertedDate.getTime()) ? null : convertedDate;
    }

    console.warn('[ExpiredLicenceGuard] Invalid subscription date value', date);
    return null;
  }
}
